/**
 *  @class
 *  @function GiftCardBlocks
 */
if (!customElements.get('gift-card-blocks')) {
  class GiftCardBlocks extends HTMLElement {
    constructor() {
      super();

      this.blocks = [];
      this.index = 0;
      this.locked = false;
      this.ticking = false;
      this.lockTimer = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-gift-cards-track]');
      if (!this.track) return;

      this.blocks = Array.from(this.track.querySelectorAll('[data-gift-card]'));
      if (this.blocks.length < 2) return;

      this.previousButton = this.querySelector('[data-gift-cards-prev]');
      this.nextButton = this.querySelector('[data-gift-cards-next]');
      this.current = this.querySelector('[data-gift-cards-current]');

      this.onPrevious = this.onPrevious.bind(this);
      this.onNext = this.onNext.bind(this);

      if (this.previousButton) this.previousButton.addEventListener('click', this.onPrevious);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.render();
      this.update();
    }

    disconnectedCallback() {
      if (this.previousButton) this.previousButton.removeEventListener('click', this.onPrevious);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);

      clearTimeout(this.lockTimer);
    }

    onPrevious() {
      this.goTo(this.index - 1);
    }

    onNext() {
      this.goTo(this.index + 1);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;

      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    onResize() {
      this.update();
    }

    goTo(index) {
      if (index < 0 || index >= this.blocks.length) return;

      const trackBox = this.track.getBoundingClientRect();
      const blockBox = this.blocks[index].getBoundingClientRect();
      const left = this.track.scrollLeft + (blockBox.left - trackBox.left);

      this.setIndex(index);
      this.lock(left);

      this.track.scrollTo({
        left: left,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    lock(target) {
      this.locked = true;
      clearTimeout(this.lockTimer);

      let attempts = 0;

      const settle = () => {
        const arrived = Math.abs(this.track.scrollLeft - target) <= 2;
        attempts += 1;

        if (arrived || attempts > 20) {
          this.locked = false;
          this.update();
          return;
        }

        this.lockTimer = setTimeout(settle, 150);
      };

      this.lockTimer = setTimeout(settle, 150);
    }

    update() {
      if (this.locked) return;

      const trackBox = this.track.getBoundingClientRect();
      let closest = 0;
      let closestDistance = Infinity;

      this.blocks.forEach((block, index) => {
        const distance = Math.abs(block.getBoundingClientRect().left - trackBox.left);

        if (distance < closestDistance) {
          closestDistance = distance;
          closest = index;
        }
      });

      this.setIndex(closest);
    }

    setIndex(index) {
      if (this.index === index) return;

      this.index = index;
      this.render();
    }

    render() {
      if (this.current) this.current.textContent = this.index + 1;

      if (this.previousButton) {
        this.previousButton.disabled = this.index === 0;
        this.previousButton.setAttribute('aria-disabled', this.index === 0);
      }

      if (this.nextButton) {
        const isLast = this.index === this.blocks.length - 1;
        this.nextButton.disabled = isLast;
        this.nextButton.setAttribute('aria-disabled', isLast);
      }
    }
  }

  customElements.define('gift-card-blocks', GiftCardBlocks);
}
